import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { motionDisabled } from '@/shared/lib/motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { HangupButton } from './HangupButton';
import { CallControls } from './CallControls';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthStore } from '@/shared/stores/auth';
import { useThemeStore } from '@/shared/stores/theme';
import { setLang } from '@/shared/i18n';
import { logout } from '@/shared/api/authApi';
import { agentApi } from '@/shared/api/agentApi';
import { useSipStore } from '@/personas/agent/stores/sipStore';
import { useSoftphoneWindow } from '@/personas/agent/stores/softphoneWindow';
import { usePageBadge } from '@/shared/stores/pageBadge';

function sipTone(state: string) {
  if (state === 'ready' || state === 'on-call') return 'ok' as const;
  if (state === 'registering') return 'info' as const;
  if (state === 'error') return 'danger' as const;
  return 'neutral' as const;
}

function sipLabel(state: string, registered: boolean): string {
  if (registered && (state === 'ready' || state === 'on-call' || state === 'on-hold')) return 'הקו מחובר';
  if (state === 'registering') return 'מתחבר…';
  if (state === 'error') return 'הקו מנותק';
  return 'לא מחובר';
}

function SipStatus() {
  const state = useSipStore((s) => s.state);
  const registered = useSipStore((s) => s.registered);
  return (
    <StatusBadge tone={sipTone(state)} dot>
      {sipLabel(state, registered)}
    </StatusBadge>
  );
}

/**
 * Softphone toggle shown to every persona (agent, admin, supervisor, super-admin).
 * It opens/closes the floating softphone — managers have no dedicated softphone
 * page, so this is their entry point.
 *
 * Hidden only for users without an extension (`noLine`). A line that FAILED to
 * register stays visible in red: on 05-06/09/2026 Asterisk realtime died and the
 * icon silently vanished for every user, which looked like a missing feature and
 * cost a day of support. A broken line must look broken, not absent.
 */
function SoftphoneToggle() {
  const registered = useSipStore((s) => s.registered);
  const state = useSipStore((s) => s.state);
  const noLine = useSipStore((s) => s.noLine);
  const error = useSipStore((s) => s.error);
  const onCall = useSipStore((s) => s.state === 'on-call' || s.state === 'ringing-in');
  const floating = useSoftphoneWindow((s) => s.floating);
  const setFloating = useSoftphoneWindow((s) => s.setFloating);
  if (noLine) return null;
  const lineDown = !registered && (state === 'error' || state === 'registering');
  if (!registered && !lineDown) return null;
  const title = lineDown
    ? state === 'registering'
      ? 'הקו מתחבר…'
      : `הקו לא רשום${error ? ` — ${error}` : ''}. לחץ לפרטים`
    : floating
      ? 'סגור סופטפון'
      : 'פתח סופטפון';
  const color = lineDown
    ? state === 'error'
      ? 'var(--danger, #B4322C)'
      : 'var(--muted, #8A9AA5)'
    : onCall
      ? 'var(--ok, #1F7A50)'
      : undefined;
  // עיצוב v2: גלולת "חייגן" כהה בקצה הסרגל, עם נקודת מצב הקו על האייקון.
  const dot = lineDown ? (state === 'error' ? 'var(--danger)' : 'var(--faint)') : '#4CC38A';
  return (
    <button
      type="button"
      className={'btn dark' + (floating ? ' active' : '')}
      title={title}
      aria-label={title}
      aria-pressed={floating}
      data-line={lineDown ? (state === 'error' ? 'down' : 'connecting') : 'up'}
      onClick={() => setFloating(!floating)}
      style={{ height: 42, padding: '0 8px', paddingInlineStart: 8, paddingInlineEnd: 16, gap: 10, borderRadius: 99, fontSize: 14 }}
    >
      <span style={{ position: 'relative', width: 30, height: 30, borderRadius: 99, display: 'grid', placeItems: 'center', background: 'rgba(127,127,127,.22)', color: color ?? 'inherit' }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: 0, insetInlineStart: 0, width: 8, height: 8, borderRadius: 99, background: dot, border: '2px solid var(--text)' }} />
        <Icon name={lineDown && state === 'error' ? 'phoneMiss' : 'phone'} size={16} />
      </span>
      <span>חייגן</span>
      {onCall && <span style={{ padding: '1px 8px', borderRadius: 99, background: 'rgba(76,195,138,.3)', fontSize: 11.5 }}>בשיחה</span>}
    </button>
  );
}

interface TopbarNotif {
  id: number;
  type: string;
  title: string;
  body?: string;
  data?: { agent_id?: number; hits?: { keyword: string; snippet?: string }[]; action?: string; channel_id?: number; link?: string } | null;
  read_at?: string | null;
  created_at: string;
}

const NOTIF_POLL_MS = 60_000;

/**
 * פעמון ההתראות: badge של לא-נקראו + תפריט נפתח עם ההתראות האחרונות
 * (המסכים /admin/notifications ו-/agent/notifications בוטלו — הפעמון הוא
 * הבית של ההתראות לשתי הפרסונות). לסופר-אדמין יש מסך התראות נפרד
 * (מסונתז) — הפעמון רק מנווט אליו.
 */
export function NotificationsBell() {
  const persona = useAuthStore((s) => s.persona);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<TopbarNotif[]>([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (persona !== 'admin' && persona !== 'agent') return;
    let alive = true;
    const tick = () => {
      void agentApi.notifications()
        .then((res) => {
          if (!alive) return;
          setItems(((res as { data?: TopbarNotif[] }).data ?? []).slice(0, 30));
          setUnread((res as { meta?: { unread?: number } }).meta?.unread ?? 0);
        })
        .catch(() => {});
    };
    tick();
    const id = window.setInterval(tick, NOTIF_POLL_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [persona]);

  // רענון מיידי בפתיחת הפאנל — שלא יוצגו התראות בנות עד דקה מאחור.
  useEffect(() => {
    if (!open || (persona !== 'admin' && persona !== 'agent')) return;
    void agentApi.notifications()
      .then((res) => {
        setItems(((res as { data?: TopbarNotif[] }).data ?? []).slice(0, 30));
        setUnread((res as { meta?: { unread?: number } }).meta?.unread ?? 0);
      })
      .catch(() => {});
  }, [open, persona]);

  // סגירה בלחיצה מחוץ לפאנל או ב-Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (persona === 'super') {
    return (
      <button type="button" className="btn ghost" style={{ padding: 6, width: 36, height: 36 }} aria-label="notifications" onClick={() => navigate('/super/notifications')}>
        <Icon name="bell" size={16} />
      </button>
    );
  }

  async function markOne(n: TopbarNotif) {
    if (n.read_at) return;
    setItems((p) => p.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnread((u) => Math.max(0, u - 1));
    await agentApi.markNotificationRead(n.id).catch(() => {});
  }

  async function markAll() {
    setItems((p) => p.map((x) => ({ ...x, read_at: x.read_at ?? new Date().toISOString() })));
    setUnread(0);
    await agentApi.markAllNotificationsRead().catch(() => {});
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className={'btn square' + (open ? ' active' : '')}
        style={{ position: 'relative' }}
        aria-label="notifications"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute', top: -5, insetInlineEnd: -5,
              minWidth: 19, height: 19, padding: '0 5px',
              borderRadius: 99, background: 'var(--danger, #D33)', color: '#fff',
              fontSize: 10.5, fontWeight: 700, lineHeight: '15px', textAlign: 'center',
              border: '2px solid var(--bg)',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', insetInlineEnd: 0,
            width: 370, maxWidth: 'calc(100vw - 24px)', zIndex: 60,
            animation: 'gcDialIn .5s var(--ease-spring) both', transformOrigin: 'top',
            background: 'var(--glass-2)', border: '1px solid var(--glass-line)',
            borderRadius: 14, boxShadow: 'inset 0 1px 0 var(--glass-hi), var(--shadow-3)',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
            <strong>התראות</strong>
            {unread > 0 && (
              <button type="button" className="btn ghost" style={{ fontSize: 'var(--t-xs)' }} onClick={() => void markAll()}>
                <Icon name="check" size={12} /> סמן הכל כנקרא
              </button>
            )}
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
                אין התראות
              </div>
            ) : items.map((n) => (
              <div
                key={n.id}
                onClick={() => void markOne(n)}
                style={{
                  display: 'flex', gap: 10, padding: '10px 14px',
                  borderBottom: '1px solid var(--line)',
                  cursor: n.read_at ? 'default' : 'pointer',
                  background: n.read_at ? 'transparent' : 'var(--surface-2)',
                }}
                title={n.read_at ? undefined : 'סמן כנקרא'}
              >
                <Icon
                  name={n.type === 'keyword_alert' ? 'bolt' : n.type === 'call_chat_opened' ? 'chat' : n.type.startsWith('call_quality.') ? 'phoneMiss' : 'bell'}
                  size={16}
                  color={n.read_at ? 'var(--muted)' : n.type === 'keyword_alert' ? 'var(--warn)' : n.type.startsWith('call_quality.') ? 'var(--danger)' : 'var(--accent)'}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: n.read_at ? 400 : 600, fontSize: 'var(--t-sm)' }}>{n.title}</div>
                  {n.body && <div style={{ color: 'var(--text-2)', fontSize: 'var(--t-xs)', marginTop: 2 }}>{n.body}</div>}
                  {n.type === 'keyword_alert' && (n.data?.hits?.length ?? 0) > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {n.data!.hits!.map((h, i) => <StatusBadge key={i} tone="warn" size="sm">{h.keyword}</StatusBadge>)}
                    </div>
                  )}
                  {n.type === 'keyword_alert' && n.data?.hits?.find((h) => h.snippet)?.snippet && (
                    <div style={{ color: 'var(--text-2)', fontSize: 'var(--t-xs)', fontStyle: 'italic', marginTop: 2 }}>
                      “{n.data.hits.find((h) => h.snippet)!.snippet}”
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                      {new Date(n.created_at).toLocaleString('he-IL')}
                    </span>
                    {persona === 'admin' && n.type === 'keyword_alert' && n.data?.action === 'listen' && n.data.agent_id != null && (
                      <button
                        type="button"
                        className="btn"
                        style={{ fontSize: 'var(--t-xs)', padding: '2px 8px' }}
                        onClick={(e) => { e.stopPropagation(); void markOne(n); setOpen(false); navigate(`/admin/live?listen_agent=${n.data!.agent_id}`); }}
                      >
                        <Icon name="headphones" size={12} /> האזן לנציג
                      </button>
                    )}
                    {/* שיחה "שקטה" (CallQualityAlerts) — ישר ליומן השיחות להאזנה. */}
                    {persona === 'admin' && n.type.startsWith('call_quality.') && n.data?.link && (
                      <button
                        type="button"
                        className="btn"
                        style={{ fontSize: 'var(--t-xs)', padding: '2px 8px' }}
                        onClick={(e) => { e.stopPropagation(); void markOne(n); setOpen(false); navigate(n.data!.link!); }}
                      >
                        <Icon name="phone" size={12} /> פתח ביומן השיחות
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}

/**
 * מעבר בהיר/כהה בעיגול שמתפשט מהכפתור (View Transitions, כמו בעיצוב).
 * דפדפן בלי תמיכה / תנועה מופחתת — החלפה מיידית.
 */
function switchTheme(btn: HTMLElement, next: 'light' | 'dark', setTheme: (t: 'light' | 'dark') => void) {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void>; finished: Promise<void> } };
  if (!doc.startViewTransition || motionDisabled()) { setTheme(next); return; }
  const b = btn.getBoundingClientRect();
  const x = b.left + b.width / 2;
  const y = b.top + b.height / 2;
  const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  document.documentElement.classList.add('gc-vt');
  const vt = doc.startViewTransition(() => {
    flushSync(() => setTheme(next));
    // ה-class מוחל ב-effect של App — מחילים גם כאן כדי שה"צילום החדש" יהיה בערכה הנכונה.
    document.documentElement.classList.toggle('theme-dark', next === 'dark');
  });
  vt.finished.finally(() => document.documentElement.classList.remove('gc-vt'));
  vt.ready.then(() => {
    document.documentElement.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
      { duration: 520, easing: 'cubic-bezier(.2,.8,.2,1)', pseudoElement: '::view-transition-new(root)' },
    );
  }).catch(() => {});
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'בעל עסק',
  admin: 'מנהל',
  manager: 'מנהל מוקד',
  supervisor: 'מנהל מוקד',
  viewer: 'צופה',
  agent: 'נציג',
};

/** אווטאר + שם + תפקיד; לחיצה פותחת תפריט קטן (שפה / התנתקות). */
function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const persona = useAuthStore((s) => s.persona);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lang = i18n.language?.startsWith('he') ? 'he' : 'en';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!user) return null;
  const roleLabel = user.role_info?.name
    ?? ROLE_LABEL[user.role]
    ?? (persona === 'super' ? 'ספק הפלטפורמה' : user.role);

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px',
    border: 0, background: 'transparent', color: 'var(--text)', fontSize: 'var(--t-sm)',
    textAlign: 'start' as const, cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 4px', border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', borderRadius: 10, fontFamily: 'inherit' }}
      >
        {/* עיגול ראשי-תיבות בצבע המותג (בעיצוב — לא צבע אקראי לפי מזהה). */}
        <span aria-hidden="true" style={{ width: 36, height: 36, flex: 'none', borderRadius: 99, display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, color: 'var(--on-accent)', background: 'var(--accent)' }}>
          {user.name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('')}
        </span>
        <span className="topbar-user-text" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, textAlign: 'start' }}>
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{user.name}</span>
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{roleLabel}</span>
        </span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 10px)', insetInlineStart: 0, zIndex: 60, minWidth: 200,
            animation: 'gcDialIn .5s var(--ease-spring) both', transformOrigin: 'top',
            background: 'var(--glass-2)', border: '1px solid var(--glass-line)', borderRadius: 12,
            boxShadow: 'inset 0 1px 0 var(--glass-hi), var(--shadow-3)', overflow: 'hidden', padding: '4px 0',
          }}
        >
          <div style={{ padding: '8px 14px 10px', borderBottom: '1px solid var(--line)', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{user.email}</div>
          <button type="button" role="menuitem" style={itemStyle} onClick={() => { setLang(lang === 'he' ? 'en' : 'he'); setOpen(false); }}>
            <Icon name="globe" size={15} /> {lang === 'he' ? 'English' : 'עברית'}
          </button>
          <button type="button" role="menuitem" style={{ ...itemStyle, color: 'var(--danger)' }} onClick={onLogout}>
            <Icon name="logout" size={15} /> {t('auth.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

export function Topbar({ title }: { title?: string }) {
  // A page (e.g. LiveMonitor) may publish a status badge to sit beside the title.
  const badge = usePageBadge((s) => s.badge);
  const persona = useAuthStore((s) => s.persona);
  const clear = useAuthStore((s) => s.clear);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const navigate = useNavigate();

  async function doLogout() {
    if (persona) await logout(persona);
    clear();
    navigate('/login', { replace: true });
  }

  return (
    <header style={{ position: 'relative', zIndex: 20, flex: 'none', padding: '14px 22px 0' }}>
      <div
        className="glass"
        style={{
          minHeight: 'var(--topbar-h)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          padding: '10px 18px',
          paddingInlineEnd: 12,
          borderRadius: 14,
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          {title && (
            <h1 key={title} className="page-enter" style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: -0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{title}</h1>
          )}
          {badge && <StatusBadge tone={badge.tone} dot={badge.dot} size="sm">{badge.label}</StatusBadge>}
        </div>

        {/* ניתוק חירום — מוצג בכל מסך כשיש שיחה פעילה, ומנתק גם בצד השרת
            כדי שערוץ שהדפדפן אינו מחזיק לא ישאיר את המשתמש תקוע "בשיחה". */}
        {/* השתקה/המתנה — רק בזמן שיחה. במסך התותח אין סופטפון צף, וזה
            המקום היחיד שבו הפקדים האלה זמינים. */}
        <CallControls />
        <HangupButton />

        {persona === 'agent' && <SipStatus />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserMenu onLogout={() => void doLogout()} />
          <NotificationsBell />
          <button
            type="button"
            className="btn square"
            onClick={(e) => switchTheme(e.currentTarget, theme === 'dark' ? 'light' : 'dark', setTheme)}
            aria-label={theme === 'dark' ? 'מצב בהיר' : 'מצב כהה'}
            title={theme === 'dark' ? 'מצב בהיר' : 'מצב כהה'}
          >
            <span key={theme} style={{ display: 'grid', animation: 'gcSpinIn .6s var(--ease-out) both' }}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
            </span>
          </button>
        </div>

        <SoftphoneSlot />
      </div>
    </header>
  );
}

/** מפריד + גלולת החייגן — המפריד מוצג רק כשיש גלולה. */
function SoftphoneSlot() {
  const noLine = useSipStore((s) => s.noLine);
  const registered = useSipStore((s) => s.registered);
  const state = useSipStore((s) => s.state);
  const visible = !noLine && (registered || state === 'error' || state === 'registering');
  if (!visible) return null;
  return (
    <>
      <div aria-hidden="true" style={{ width: 1, height: 28, background: 'var(--glass-line-2)' }} />
      <SoftphoneToggle />
    </>
  );
}
